import type { Editor as TinyMCEEditorInstance } from 'tinymce';
import { v4 as uuidv4 } from 'uuid';
import { AIResponse, EditorRefObject } from '../types';
import { getElement } from './generalizationHelpers';

const MAPPING_STATE_ATTRS = [
  'data-highlight',
  'data-highlighted',
  'data-mce-selected',
  'data-sourceid',
  'data-selectionid',
  'data-original-bg-color',
  'data-merged-cell',
  'data-comment',
];

const CLONED_TABLE_UI_SELECTORS = '[data-comment="true"]';

export type DuplicateTableResult = {
  oldToNewNodeIdMap: Record<string, string>;
  newRootTable: HTMLTableElement;
};

export const getTableForTargetNodeId = (
  editorRef: EditorRefObject,
  nodeId: string,
): HTMLTableElement | null => {
  const element = getElement(editorRef, nodeId);
  return (element?.closest('table') as HTMLTableElement | null) ?? null;
};

const stripMappingUiState = (element: HTMLElement) => {
  MAPPING_STATE_ATTRS.forEach((attr) => element.removeAttribute(attr));
  element.querySelectorAll(CLONED_TABLE_UI_SELECTORS).forEach((uiNode) => uiNode.remove());
  element.classList.remove('locked-node');
  element.removeAttribute('title');
  element.removeAttribute('contenteditable');
  element.style.removeProperty('background-color');
  element.style.removeProperty('cursor');
};

/** Assign fresh target document ids to every mappable cell in a table clone. */
export const assignNewTargetNodeIds = (
  table: HTMLTableElement,
  docId: string,
): Record<string, string> => {
  const oldToNewNodeIdMap: Record<string, string> = {};

  table.querySelectorAll<HTMLElement>('td, th').forEach((cell) => {
    stripMappingUiState(cell);
    const oldId = cell.getAttribute('data-nodeid');
    const newId = uuidv4();
    if (oldId) {
      oldToNewNodeIdMap[oldId] = newId;
    }
    cell.setAttribute('data-nodeid', newId);
    cell.setAttribute('data-docid', docId);
    cell.setAttribute('data-newnode', 'true');
  });

  table.querySelectorAll<HTMLElement>('[data-nodeid]').forEach((element) => {
    if (element.tagName === 'TD' || element.tagName === 'TH') {
      return;
    }
    stripMappingUiState(element);
    const oldId = element.getAttribute('data-nodeid');
    if (!oldId) {
      return;
    }
    const newId = uuidv4();
    oldToNewNodeIdMap[oldId] = newId;
    element.setAttribute('data-nodeid', newId);
    element.setAttribute('data-newnode', 'true');
  });

  return oldToNewNodeIdMap;
};

/**
 * Deep-clone a table, assign fresh node ids to all mappable cells (and nested nodes),
 * and insert the copy immediately after the source table.
 */
export const duplicateTableElement = (
  sourceTable: HTMLTableElement,
  docId: string,
): DuplicateTableResult => {
  const clone = sourceTable.cloneNode(true) as HTMLTableElement;
  const oldToNewNodeIdMap = assignNewTargetNodeIds(clone, docId);
  sourceTable.parentNode?.insertBefore(clone, sourceTable.nextSibling);

  return { oldToNewNodeIdMap, newRootTable: clone };
};

/** Insert a duplicated table as a single TinyMCE undo step (Ctrl+Z / toolbar Undo). */
export const duplicateTableInEditor = (
  editor: TinyMCEEditorInstance,
  sourceTable: HTMLTableElement,
  docId: string,
): DuplicateTableResult => {
  let result!: DuplicateTableResult;
  editor.undoManager.transact(() => {
    result = duplicateTableElement(sourceTable, docId);
  });
  editor.nodeChanged();
  return result;
};

const nodeExistsInBody = (body: HTMLElement, nodeId: string): boolean =>
  !!body.querySelector(`[data-nodeid="${CSS.escape(nodeId)}"]`);

/** Drop AI entries whose target nodes are no longer in the editor (e.g. after Undo). */
export const pruneAiResponseForMissingNodes = (
  aiResponse: AIResponse,
  body: HTMLElement,
): AIResponse => {
  let changed = false;
  const pruned: AIResponse = { ...aiResponse };

  Object.keys(pruned).forEach((nodeId) => {
    if (!nodeExistsInBody(body, nodeId)) {
      delete pruned[nodeId];
      changed = true;
    }
  });

  return changed ? pruned : aiResponse;
};

export type DuplicateTableUndoContext = {
  anchorNodeId: string;
  oldToNewNodeIdMap: Record<string, string>;
  scroll: { scrollTop: number; scrollLeft: number };
};

export const getTargetEditorWindowScroll = (
  editor: TinyMCEEditorInstance,
): { scrollTop: number; scrollLeft: number } => {
  const win = editor.getWin();
  return {
    scrollTop: win?.scrollY ?? 0,
    scrollLeft: win?.scrollX ?? 0,
  };
};

export const wasDuplicateTableUndone = (
  body: HTMLElement,
  oldToNewNodeIdMap: Record<string, string> | null,
): boolean => {
  if (!oldToNewNodeIdMap) {
    return false;
  }

  return Object.values(oldToNewNodeIdMap).every((nodeId) => !nodeExistsInBody(body, nodeId));
};

/** Restore target scroll/selection after Ctrl+Z removes a duplicated or transferred table. */
/** Resolve the undo context that matches the table duplication just undone. */
export const resolveUndoneDuplicateTableContext = (
  body: HTMLElement,
  undoContexts: DuplicateTableUndoContext[],
): DuplicateTableUndoContext | null => {
  for (let index = undoContexts.length - 1; index >= 0; index -= 1) {
    const context = undoContexts[index];
    if (wasDuplicateTableUndone(body, context.oldToNewNodeIdMap)) {
      return context;
    }
  }
  return null;
};

export const restoreTargetViewAfterDuplicateTableUndo = (
  editor: TinyMCEEditorInstance,
  context: DuplicateTableUndoContext,
): string | null => {
  const body = editor.getBody();
  if (!body || !wasDuplicateTableUndone(body, context.oldToNewNodeIdMap)) {
    return null;
  }

  const anchorEl = body.querySelector(
    `[data-nodeid="${CSS.escape(context.anchorNodeId)}"]`,
  ) as HTMLElement | null;
  if (!anchorEl) {
    return null;
  }

  editor.getWin()?.scrollTo(context.scroll.scrollLeft, context.scroll.scrollTop);
  editor.selection.select(anchorEl, true);
  editor.selection.collapse(true);

  return context.anchorNodeId;
};

/** Copy AI suggestion entries from duplicated cells onto their new node ids. */
export const copyAiResponseForDuplicatedTable = (
  oldToNewNodeIdMap: Record<string, string>,
  aiResponse: AIResponse,
  resolveNodeId: (nodeId: string) => string,
): AIResponse => {
  const copied: AIResponse = {};

  Object.entries(oldToNewNodeIdMap).forEach(([oldId, newId]) => {
    const resolvedOldId = resolveNodeId(oldId);
    const entries = aiResponse[resolvedOldId] ?? aiResponse[oldId];
    if (entries) {
      copied[newId] = JSON.parse(JSON.stringify(entries)) as AIResponse[string];
    }
  });

  return copied;
};
